import type { BaseType } from 'antd/lib/typography/Base';
import { useFormikContext } from 'formik';
import { type JSX, useCallback, useMemo } from 'react';

import './SpecificationIntervalSimpleField.scss';

import { SelectField } from 'src/components/base/fields/SelectField';
import type { MeasureUnitIntervalValue } from 'src/components/base/fields/SpecificationField/SpecificationIntervalField/SpecificationIntervalField.types';
import { TextInputField } from 'src/components/base/fields/TextInputField';
import { ValidationError } from 'src/components/base/fields/common/ValidationError/ValidationError';
import { cNs } from 'src/helpers';
import type { ISelectOption } from 'src/types';
import {
  SPECIFICATION_MEASURE_UNIT_MAPPER,
  SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER,
  type SpecificationMeasureUnit,
} from 'src/types/reference/SpecificationMeasureUnit';

function OptionRender({ value }: { value?: string | number }) {
  return `${SPECIFICATION_MEASURE_UNIT_MAPPER[value as SpecificationMeasureUnit]} (${SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[value as SpecificationMeasureUnit]})`;
}

type SpecificationIntervalSimpleFieldProps = {
  commonFieldProps: {
    errorType: BaseType;
    name: string;
    label?: string;
    description?: string | null;
    disabled?: boolean;
  };

  allowedMeasureUnits: SpecificationMeasureUnit[];

  containerClassName?: string;
  className?: string;
};

export function SpecificationIntervalSimpleField({
  commonFieldProps,
  allowedMeasureUnits,
  containerClassName,
  className,
}: Readonly<SpecificationIntervalSimpleFieldProps>): JSX.Element {
  const { setValues } = useFormikContext<Record<string, MeasureUnitIntervalValue | null>>();

  const measureUnitsOptions: ISelectOption[] = useMemo(
    () =>
      allowedMeasureUnits.map((measureUnit) => ({
        label: SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[measureUnit],
        value: measureUnit,
      })),
    [allowedMeasureUnits]
  );

  const handleMeasureUnitChange = useCallback(() => {
    void setValues((prev) => ({
      ...prev,

      [commonFieldProps.name]: null,
    }));
  }, [setValues, commonFieldProps.name]);

  return (
    <div className={cNs('specification-interval-simple-field__container', containerClassName)}>
      <div className="specification-interval-simple-field__label">{commonFieldProps.label}</div>
      {commonFieldProps.description && (
        <div className="specification-interval-simple-field__description">{commonFieldProps.description}</div>
      )}
      <div className={cNs('flex row nowrap gap-small', className)}>
        <TextInputField
          name={`${commonFieldProps.name}.minValue`}
          type="number"
          placeholder="Min Value"
          isShowErrorMessage={false}
          disabled={commonFieldProps.disabled}
        />
        <TextInputField
          name={`${commonFieldProps.name}.maxValue`}
          type="number"
          placeholder="Max Value"
          isShowErrorMessage={false}
          disabled={commonFieldProps.disabled}
        />
        <SelectField
          style={{ minWidth: 65 }}
          name={`${commonFieldProps.name}.measureUnit`}
          options={measureUnitsOptions}
          onChange={handleMeasureUnitChange}
          disabled={commonFieldProps.disabled}
          popupMatchSelectWidth={false}
          optionRender={({ value }) => <OptionRender value={value} />}
        />
      </div>
      <div className="relative">
        <ValidationError {...commonFieldProps} />
      </div>
    </div>
  );
}
